import * as React from "react";
import { useCallback } from "react";
import { styled, Theme } from "@mui/material";
import { TopNavigation, TopNavigationProps } from "../layouts/topNavigation";
import { MobileView } from "../../util/aKaunGeneralUtil";
import { PageMargin } from "./layoutConsts";
import { useHistory } from "react-router-dom";
import { useDiagnosticNavigation } from "./useDiagnosticNavigation";
import CssBaseline from "@mui/material/CssBaseline/CssBaseline";
import IconButton from "@mui/material/IconButton";
import clsx from "clsx";
import { RosebudColorMap } from "./rosebud/rosebudColorMap";
import { makeStyles, createStyles } from "@mui/styles";
import MenuIcon from "@mui/icons-material/Menu";
import {
  DiagnosticAgentType,
  useDiagnosticAgentType,
} from "../../context/diagnosticAgent";
import { ShareCaseMenuItem } from "./shareCaseMenuItem";
import { SaveCaseButton } from "./common/buttons/saveCaseButton";
import { SignInOrUserSection } from "./common/buttons/signInOrUserSection";
import { SendFeedbackButton } from "./common/buttons/sendFeedbackButton";
import { StartOverButton } from "./common/buttons/startOverButton";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import {
  commonBoxShadowHoverStyle,
  commonBoxShadowStyle,
} from "fe-common/ui/boxShadow";
import { useScreenSize } from "./util/useScreenSize";
import { PatientSummaryButton } from "./common/buttons/patientSummaryButton";
import { KahunLogo } from "./common/kahunLogo";

//region [[ Styles ]]
export const AgentNavBarHeight = 69;
export const AgentMobileNavBarHeight = 64;

const AgentTopNavigationView = styled(TopNavigation)(
  ({ theme, pathname }: { theme?: any; pathname: string }) => ({
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    alignItems: "center",
    background: "white",
    height: `${AgentNavBarHeight}px`,
    borderRadius: "0px 0px 10px 10px",
    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
    [theme.breakpoints.down(MobileView)]: {
      height: `${AgentMobileNavBarHeight}px`,
      backgroundColor: "rgb(246, 254, 255)",
    },
    "& .left": {
      [theme.breakpoints.down(MobileView)]: {
        height: "40px",
        "& svg": { height: "40px" },
      },
      "& h4": {
        position: "absolute",
        paddingLeft: PageMargin,
        color: "white",
      },
    },
    "& .right": {
      width: "60%",
    },
  }),
);

const useStyles = makeStyles((theme?: Theme) =>
  createStyles({
    menuButton: {
      fontSize: "xxx-large",
    },
  }),
);

const AgentContainerTopNavigation = styled("div")(({ theme }) => ({
  width: "100%",
  "& .left h4": {
    paddingLeft: PageMargin,
    [theme.breakpoints.down(MobileView)]: {
      paddingLeft: "auto",
      right: theme.spacing(4),
    },
  },
}));

const RightSection = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  "& .action-button": {
    marginRight: "10px",
    ...commonBoxShadowStyle,
  },
  "& .action-button:hover": {
    ...commonBoxShadowHoverStyle,
  },
}));

const MenuButtonIcon = styled(MenuIcon)({
  color: "white",
});

const ErBadge = styled("span")({
  borderRadius: "6px",
  width: "100px",
  color: RosebudColorMap.red,
  border: `1px solid ${RosebudColorMap.red}`,
  fontWeight: 200,
  fontSize: "0.1em",
  padding: "0 3px",
});

const Logo = styled(KahunLogo)({
  "& svg": { height: "37.5px" },
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface AgentTopNavigationProps extends TopNavigationProps {
  centeredLogo?: boolean;
  hideActionsBar?: boolean;
  // Default is false
  responsive?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]

//endregion [[ Functions ]]

export const AgentTopNavigation = React.memo(
  ({ centeredLogo, responsive, ...props }: AgentTopNavigationProps) => {
    const history = useHistory();
    const { menuOpen, setMenuOpen } = useDiagnosticNavigation();
    const classes = useStyles();
    const { isMobile } = useScreenSize();

    const handleDrawerClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setMenuOpen(!menuOpen);
      },
      [setMenuOpen, menuOpen],
    );

    const [agentType] = useDiagnosticAgentType();

    const camel2title = React.useCallback(
      (camelCase) =>
        camelCase
          .replace(/([A-Z])/g, (match) => ` ${match}`)
          .replace(/^./, (match) => match.toUpperCase()),
      [],
    );

    const middleSection = React.useCallback(
      () => (
        <>
          {centeredLogo && <KahunLogo small hideBackground />}
          {agentType !== DiagnosticAgentType.InternalMedicine && (
            <ErBadge>{camel2title(agentType)}</ErBadge>
          )}
        </>
      ),
      [centeredLogo, agentType, camel2title],
    );

    const buttonsSection = useCallback(
      () =>
        props.hideActionsBar ? null : (
          <RightSection>
            <PatientSummaryButton className={"action-button"} onlyIcon />
            <SendFeedbackButton className={"action-button"} onlyIcon />
            <SaveCaseButton className={"action-button"} onlyIcon />
            <ShareCaseMenuItem className={"action-button"} onlyIcon />
            <StartOverButton className={"action-button"} onlyIcon />
            <SignInOrUserSection />
          </RightSection>
        ),
      [props.hideActionsBar],
    );

    const menuArea = useCallback(
      () => (
        <>
          <CssBaseline />
          <IconButton
            color="primary"
            aria-label="open drawer"
            onClick={handleDrawerClick}
            edge="start"
            className={clsx(classes.menuButton, menuOpen)}
          >
            <MenuButtonIcon style={{ color: KahunColorMap.Default }} />
          </IconButton>
          {!centeredLogo && <Logo />}
        </>
      ),
      [handleDrawerClick, classes, menuOpen, centeredLogo],
    );

    if (!responsive && isMobile) return null;

    return (
      <AgentContainerTopNavigation className={props.className}>
        <AgentTopNavigationView
          className={"top-navigation"}
          pathname={history.location.pathname}
          leftSectionItems={menuArea()}
          middleSectionItems={middleSection()}
          rightSectionItems={buttonsSection()}
        />
      </AgentContainerTopNavigation>
    );
  },
);
